/**
 * Twitch Streaming Service - Web Implementation
 * 
 * Implements Twitch integration using IRC WebSocket
 * - Anonymous connection (justinfan) - no OAuth required
 * - Follows unified streaming platform architecture
 * Ported from beemi-react-native for web compatibility.
 */

import { StreamingPlatformService } from '../base/StreamingPlatformService';
import {
  StreamingComment,
  StreamingMetrics,
  StreamingRoomInfo,
  PlatformConnectionOptions,
  StreamValidationResult
} from '../../../types/streaming';

// Twitch-specific types
export interface TwitchConnectionOptions extends PlatformConnectionOptions {
  streamerUsername: string;
  accessToken?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onComment?: (comment: StreamingComment) => void;
  onError?: (error: Error) => void;
  onMetricsUpdate?: (metrics: StreamingMetrics) => void;
  onRoomInfo?: (roomInfo: StreamingRoomInfo) => void;
}

interface TwitchIRCMessage {
  raw: string;
  tags: { [key: string]: string };
  prefix: string;
  command: string;
  params: string[];
}

interface TwitchChatMessage {
  username: string;
  displayName: string;
  message: string;
  channel: string;
  userId: string;
  color: string;
  badges: string[];
  timestamp: number;
  isModerator: boolean;
  isSubscriber: boolean;
  isVip: boolean;
  isBroadcaster: boolean;
  raw: TwitchIRCMessage;
}

export class TwitchStreamingService extends StreamingPlatformService {
  private ws: WebSocket | null = null;
  private currentStreamerUsername: string = '';
  private connectionOptions: TwitchConnectionOptions | null = null;
  private isAuthenticated: boolean = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private processedMessageIds = new Set<string>();

  private onConnectionEstablished: ((value: void) => void) | null = null;
  private onConnectionFailed: ((reason: any) => void) | null = null;

  constructor() {
    super('twitch');
  }

  /**
   * Check if Twitch stream is live
   * Uses NicheProwler as fallback when API is not available
   */
  private async checkLiveStatusWithFallbacks(username: string): Promise<boolean> {
    console.log('🟣 [Twitch] Checking live status for:', username);

    // Try NicheProwler scraping as primary method (no API key needed)
    try {
      const scraperResult = await this.checkLiveStatusViaNicheProwler(username);
      if (scraperResult !== null) {
        console.log('🟣 [Twitch] Scraper method succeeded → isLive:', scraperResult);
        return scraperResult;
      }
    } catch (error) {
      console.warn('🟣 [Twitch] Scraper method failed:', error);
    }

    // Fallback: Assume live (will fail on IRC join if not)
    console.log('🟣 [Twitch] All methods failed, assuming live');
    return true;
  }

  /**
   * Check live status using NicheProwler website scraping
   */
  private async checkLiveStatusViaNicheProwler(username: string): Promise<boolean | null> {
    try {
      const url = `https://www.nicheprowler.com/tools/twitch/twitch-username-checker?query=${encodeURIComponent(username)}`;
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        return null;
      }

      const html = await response.text();

      // Look for live status in HTML
      const liveStatusMatch = html.match(/<span class="font-medium">Live Status:<\/span>\s*<div[^>]*>([^<]+)<\/div>/i);
      
      if (liveStatusMatch) {
        const status = liveStatusMatch[1].trim();
        return status.toLowerCase() === 'live';
      }

      // Alternative pattern
      const liveBadgeMatch = html.match(/border-transparent bg-primary[^>]*>Live<\/div>/i);
      if (liveBadgeMatch) {
        return true;
      }

      const offlineBadgeMatch = html.match(/Offline<\/div>/i);
      if (offlineBadgeMatch) {
        return false;
      }

      return null;
    } catch (error) {
      console.warn('🟣 [Twitch] NicheProwler check failed:', error);
      return null;
    }
  }

  async connect(options: TwitchConnectionOptions): Promise<void> {
    // Prevent multiple connections
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('⚠️ Already connected to Twitch, disconnecting first...');
      this.disconnect();
    }

    this.connectionOptions = options;
    this.currentStreamerUsername = options.streamerUsername.replace(/^@/, '').toLowerCase();

    this.setConnectionState('connecting');

    try {
      // Validate stream is live before connecting
      console.log('🟣 [Twitch] Validating stream is live...');
      const isLive = await this.checkLiveStatusWithFallbacks(this.currentStreamerUsername);
      
      if (!isLive) {
        const errorMessage = `No active live stream found for '${this.currentStreamerUsername}'`;
        const platformError = this.createPlatformError(errorMessage, 'STREAM_NOT_LIVE');
        this.handleError(platformError);
        this.setConnectionState('disconnected');
        throw platformError;
      }
      
      console.log('🟣 [Twitch] Stream is live, connecting to IRC...');
      await this.connectToIRC();

    } catch (error) {
      const platformError = error instanceof Error ? error : this.createPlatformError(
        `Failed to connect to Twitch: ${error}`,
        'CONNECTION_ERROR'
      );
      this.handleError(platformError);
      this.setConnectionState('disconnected');
      throw platformError;
    }
  }

  private async connectToIRC(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket('wss://irc-ws.chat.twitch.tv:443');

        this.ws.onopen = () => {
          console.log('🟢 Connected to Twitch IRC WebSocket');
          this.authenticateToIRC();
        };

        this.ws.onmessage = (event) => {
          this.handleIRCMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('🔴 Twitch IRC WebSocket closed:', event.code, event.reason);
          this.cleanup();
          this.setConnectionState('disconnected');
          this.connectionOptions?.onDisconnect?.();
        };

        this.ws.onerror = (error) => {
          console.error('🔴 Twitch IRC WebSocket error:', error);
          const platformError = this.createPlatformError('IRC connection failed', 'CONNECTION_ERROR');
          this.handleError(platformError);
          this.connectionOptions?.onError?.(platformError);
          reject(platformError);
        };

        this.onConnectionEstablished = resolve;
        this.onConnectionFailed = reject;

      } catch (error) {
        const platformError = this.createPlatformError(
          `Failed to connect to Twitch IRC: ${error}`,
          'CONNECTION_ERROR'
        );
        this.handleError(platformError);
        reject(platformError);
      }
    });
  }

  private authenticateToIRC(): void {
    if (!this.ws) return;

    const anonymousNick = `justinfan${Math.floor(Math.random() * 999999)}`;
    
    console.log('🔐 Authenticating to Twitch IRC as:', anonymousNick);

    this.sendRaw(`PASS SCHMOOPIIE`);
    this.sendRaw(`NICK ${anonymousNick}`);
    this.sendRaw('CAP REQ :twitch.tv/tags twitch.tv/commands');
  }

  private sendRaw(message: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(message + '\r\n');
    }
  }

  private handleIRCMessage(rawData: string): void {
    const lines = rawData.trim().split(/\r?\n/);
    
    for (const line of lines) {
      if (!line.trim()) continue;
      
      const message = this.parseIRCMessage(line);
      
      switch (message.command) {
        case '001': // Welcome message
          console.log('✅ IRC Welcome message received');
          this.handleWelcome();
          break;
          
        case 'PING':
          this.handlePing(message);
          break;
          
        case 'PRIVMSG':
          this.handleChatMessage(message);
          break;
          
        case 'JOIN':
          this.handleJoin(message);
          break;
          
        case 'PART':
          this.handlePart(message);
          break;
      }
    }
  }

  private parseIRCMessage(line: string): TwitchIRCMessage {
    const message: TwitchIRCMessage = {
      raw: line,
      tags: {},
      prefix: '',
      command: '',
      params: []
    };

    let remaining = line;

    // Parse tags
    if (remaining.startsWith('@')) {
      const tagEnd = remaining.indexOf(' ');
      const tagString = remaining.slice(1, tagEnd);
      remaining = remaining.slice(tagEnd + 1);
      
      tagString.split(';').forEach(tag => {
        const [key, value] = tag.split('=');
        message.tags[key] = value || '';
      });
    }

    // Parse prefix
    if (remaining.startsWith(':')) {
      const prefixEnd = remaining.indexOf(' ');
      message.prefix = remaining.slice(1, prefixEnd);
      remaining = remaining.slice(prefixEnd + 1);
    }

    // Parse command and parameters
    const parts = remaining.split(' ');
    message.command = parts[0];
    message.params = parts.slice(1);

    // Handle trailing parameter
    const colonIndex = message.params.findIndex(param => param.startsWith(':'));
    if (colonIndex !== -1) {
      const trailing = message.params.slice(colonIndex).join(' ').slice(1);
      message.params = message.params.slice(0, colonIndex);
      message.params.push(trailing);
    }

    return message;
  }

  private handleWelcome(): void {
    console.log('✅ Twitch IRC authentication successful');
    
    const channel = `#${this.currentStreamerUsername}`;
    console.log(`🚪 Joining channel: ${channel}`);
    this.sendRaw(`JOIN ${channel}`);
    
    // Keep connection alive
    this.pingInterval = setInterval(() => {
      this.sendRaw('PING :tmi.twitch.tv');
    }, 30000);

    this.isAuthenticated = true;
  }

  private handlePing(message: TwitchIRCMessage): void {
    const server = message.params[0] || 'tmi.twitch.tv';
    this.sendRaw(`PONG :${server}`);
  }

  private handleJoin(message: TwitchIRCMessage): void {
    const channel = message.params[0];
    console.log(`✅ Joined Twitch channel: ${channel}`);
    
    this.setConnectionState('connected');
    
    // Update metrics
    this.updateMetrics({
      platform: 'twitch',
      viewerCount: 0,
      likeCount: 0,
      giftCount: 0,
      shareCount: 0,
      commentCount: 0,
      memberCount: 0
    });

    // Update room info
    const roomInfo: StreamingRoomInfo = {
      platform: 'twitch',
      isLive: true,
      roomId: this.currentStreamerUsername,
      channelId: this.currentStreamerUsername,
      title: `${this.currentStreamerUsername}'s stream`,
      streamImageUrl: `https://static-cdn.jtvnw.net/previews-ttv/live_user_${this.currentStreamerUsername.toLowerCase()}-640x360.jpg`,
      streamerInfo: {
        uniqueId: this.currentStreamerUsername,
        nickname: this.currentStreamerUsername,
        userId: '',
        profileImageUrl: undefined // Twitch IRC doesn't provide profile image
      },
      rawData: { channel }
    };
    
    console.log('🎮 [Twitch] Room info setup:', {
      title: roomInfo.title,
      streamImageUrl: roomInfo.streamImageUrl
    });
    
    this.handleRoomInfo(roomInfo);
    this.connectionOptions?.onRoomInfo?.(roomInfo);

    this.connectionOptions?.onConnect?.();
    this.onConnectionEstablished?.();
  }

  private handlePart(message: TwitchIRCMessage): void {
    const channel = message.params[0];
    console.log(`🚪 Left Twitch channel: ${channel}`);
  }

  private handleChatMessage(message: TwitchIRCMessage): void {
    const channel = message.params[0];
    const text = message.params[1] || '';
    
    const username = message.prefix.split('!')[0] || 'unknown';
    const displayName = message.tags['display-name'] || username;
    const userId = message.tags['user-id'] || '';
    const color = message.tags['color'] || '#FFFFFF';
    const badges = message.tags['badges'] ? message.tags['badges'].split(',').map(b => b.split('/')[0]) : [];
    const isModerator = badges.includes('moderator');
    const isSubscriber = badges.includes('subscriber');
    const isVip = badges.includes('vip');
    const isBroadcaster = badges.includes('broadcaster');

    const chatMessage: TwitchChatMessage = {
      username,
      displayName,
      message: text,
      channel,
      userId,
      color,
      badges,
      timestamp: Date.now(),
      isModerator,
      isSubscriber,
      isVip,
      isBroadcaster,
      raw: message
    };

    console.log(`💬 ${displayName}: ${text}`);

    const messageId = message.tags['id'] || `${userId}_${Date.now()}`;

    // Duplicate prevention
    if (this.processedMessageIds.has(messageId)) {
      return;
    }
    this.processedMessageIds.add(messageId);

    // Convert to unified format
    const streamingComment: StreamingComment = {
      id: messageId,
      username: displayName,
      comment: text,
      timestamp: Date.now(),
      platform: 'twitch',
      metadata: {
        userId,
        color,
        badges,
        isModerator,
        isSubscriber,
        isVip,
        isBroadcaster
      }
    };

    // Update comment count
    this.updateMetrics({
      commentCount: (this.streamMetrics.commentCount || 0) + 1
    });

    // Notify listeners
    this.connectionOptions?.onComment?.(streamingComment);
    this.callbacks.onComment?.(streamingComment);
  }

  private cleanup(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    this.processedMessageIds.clear();
    this.isAuthenticated = false;
  }

  disconnect(): void {
    this.cleanup();
    
    if (this.ws) {
      if (this.isAuthenticated) {
        this.sendRaw(`PART #${this.currentStreamerUsername}`);
      }
      
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.setConnectionState('disconnected');
  }

  validateStreamRequirements(): StreamValidationResult {
    return {
      isLive: this.connectionState === 'connected',
      platform: 'twitch',
      requirements: {
        isConnectedToIRC: this.connectionState === 'connected',
        hasJoinedChannel: this.isAuthenticated,
        isReceivingMessages: this.connectionState === 'connected'
      },
      issues: []
    };
  }

  isStreamConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.isAuthenticated;
  }

  getCurrentChannel(): string {
    return `#${this.currentStreamerUsername}`;
  }
}

// Singleton instance
export const twitchService = new TwitchStreamingService();

